import { LumenIcon, LumenTooltip } from '@photoshelter/socialie-components';
import { LumenIconName } from '@photoshelter/socialie-components/dist/lib/components/LumenIconTypes';
import CollapsibleSection from 'components/CollapsibleSection';
import formatDate from 'helpers/formatDate';
import { useTranslation } from 'react-i18next';
import Submission from 'types/Submission';

interface Props {
  submission: Submission;
}

export default function SubmissionSidebarRoutingEvents(props: Props) {
  const { submission } = props;
  const { t } = useTranslation();
  if (!submission.routingEvents?.length) return null;

  return (
    <div data-qa="routing-events">
      <CollapsibleSection
        title={t('ugcRequestShow.submissions.routingEvents.heading')}
      >
        {submission.routingEvents.map((routingEvent) => {
          return (
            <div
              key={routingEvent.id}
              className="text-14 mb-2 flex flex-col gap-0.5 last:mb-0"
              data-qa={`routing-event-${routingEvent.id}`}
            >
              <div className="text-12 leading-24">
                <span className="text-muted-foreground font-semibold">
                  {t(
                    `ugcRequestShow.submissions.routingEvents.destinations.${routingEvent.destinationType}`
                  )}
                </span>{' '}
                {routingEvent.status === 'pending' ? (
                  <span>
                    ({t('ugcRequestShow.submissions.routingEvents.pending')})
                  </span>
                ) : routingEvent.status === 'in_progress' ? (
                  <span>
                    ({t('ugcRequestShow.submissions.routingEvents.inProgress')})
                  </span>
                ) : routingEvent.status === 'failed' ? (
                  <span className="text-error">
                    ({t('ugcRequestShow.submissions.routingEvents.failed')})
                  </span>
                ) : (
                  formatDate(routingEvent.updatedAt)
                )}
              </div>

              <RoutingEventDetail
                label={t('ugcRequestShow.submissions.routingEvents.account')}
                iconId="building-outline"
              >
                {routingEvent.destinationLabel}
              </RoutingEventDetail>

              <RoutingEventDetail
                label={t('ugcRequestShow.submissions.routingEvents.event')}
                iconId="send-outline"
              >
                {t(
                  `ugcRequestShow.submissions.routingEvents.eventTypes.${routingEvent.event}`,
                  { creator: routingEvent.creator?.displayName }
                )}
              </RoutingEventDetail>

              {routingEvent.displayPath && (
                <RoutingEventDetail
                  label={t(
                    'ugcRequestShow.submissions.routingEvents.destination'
                  )}
                  iconId="collection-outline"
                >
                  {
                    routingEvent.displayPath.split('/')[
                      routingEvent.displayPath.split('/').length - 1
                    ]
                  }
                </RoutingEventDetail>
              )}

              {!!routingEvent.externalUrl && (
                <RoutingEventDetail
                  label={t(
                    'ugcRequestShow.submissions.routingEvents.externalUrl'
                  )}
                  iconId="launch"
                >
                  <a
                    href={routingEvent.externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-underline text-brandPrimaryHighlight overflow-hidden truncate underline hover:opacity-90"
                  >
                    {t('ugcRequestShow.submissions.routingEvents.externalUrl', {
                      destination: t(
                        `ugcRequestShow.submissions.routingEvents.destinations.${routingEvent.destinationType}`
                      ),
                    })}
                  </a>
                </RoutingEventDetail>
              )}
            </div>
          );
        })}
      </CollapsibleSection>
    </div>
  );
}

function RoutingEventDetail({
  iconId,
  label,
  children,
}: {
  iconId: LumenIconName;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-3 items-start gap-1 overflow-hidden">
      <LumenTooltip content={label} theme="dark" side="left" arrow>
        <button>
          <LumenIcon
            id={iconId}
            className="text-muted-foreground mt-[3px] h-2 w-2 shrink-0"
            aria-hidden="true"
          />
        </button>
      </LumenTooltip>
      <span className="overlow-hidden flex-1">{children}</span>
    </div>
  );
}
